import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { MemberSidebarLayout } from './SidebarLayout';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import {
  Copy, CheckCircle, Users, Loader2, DollarSign,
} from 'lucide-react';
import { EarningStreakCard } from './EarningStreakCard';
import { ReferralShareButton } from './ReferralShareButton';
import { toast } from 'sonner';
import { useWebSocket } from '../hooks/useWebSocket';
import { DualPrice } from './DualPrice';

export function ReferralsPage() {
  const { user, api } = useAuth();
  const [stats, setStats] = useState(null);
  const [tree, setTree] = useState({ level_1: [], level_2: [], totals: { level_1: 0, level_2: 0 } });
  const [commissions, setCommissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [withdrawOpen, setWithdrawOpen] = useState(false);
  const [withdrawAmount, setWithdrawAmount] = useState('');
  const [payoutMethod, setPayoutMethod] = useState('gcash'); // gcash | paymaya | bank
  const [savedMethods, setSavedMethods] = useState([]);
  const [selectedMethodId, setSelectedMethodId] = useState('');
  const [useManualEntry, setUseManualEntry] = useState(false);
  const [bankName, setBankName] = useState('');
  const [bankAccountName, setBankAccountName] = useState('');
  const [bankAccountNumber, setBankAccountNumber] = useState('');
  const [withdrawing, setWithdrawing] = useState(false);
  const [banks, setBanks] = useState([]);
  const [bankSearch, setBankSearch] = useState('');
  const [showBankDropdown, setShowBankDropdown] = useState(false);
  const [payoutSchedule, setPayoutSchedule] = useState(null);
  // Latest withdrawal receipt, kept for future PDF download flow
  const [, setLastWithdrawalReceipt] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const [statsRes, treeRes, commissionsRes, banksRes, scheduleRes, methodsRes] = await Promise.all([
        api.get('/api/referrals/stats'),
        api.get('/api/referrals/tree'),
        api.get('/api/referrals/commissions'),
        api.get('/api/banks'),
        // Show "next payout date" to members
        api.get('/api/config/payout-schedule').catch(() => null),
        api.get('/api/payouts/methods').catch(() => ({ data: { methods: [] } })),
      ]);
      setStats(statsRes.data);
      const t = treeRes.data || {};
      setTree({
        level_1: t.level_1 || t.tree || [],
        level_2: t.level_2 || [],
        totals: t.totals || { level_1: (t.tree || []).length, level_2: 0 },
      });
      setCommissions(commissionsRes.data.commissions || []);
      setBanks(banksRes.data.banks || []);
      if (scheduleRes?.data?.computed) setPayoutSchedule(scheduleRes.data.computed);
      const methods = methodsRes.data.methods || [];
      setSavedMethods(methods);
      // Default to the user's default payout method when opening
      const def = methods.find((m) => m.is_default) || methods[0];
      if (def) setSelectedMethodId(def.id);
      // If user has no saved methods, fall back to manual entry
      setUseManualEntry(methods.length === 0);
    } catch (error) {
      console.error('Failed to fetch referral data:', error);
    } finally {
      setLoading(false);
    }
  }, [api]);

  
  useEffect(() => { fetchData(); }, [fetchData]);

  const handleWsMessage = useCallback((msg) => {
    if (msg.type === 'wallet_update' || msg.type === 'commission') {
      toast.info('Referral data updated');
      fetchData();
    }
  }, [fetchData]);

  useWebSocket(user?.id, handleWsMessage);

  const copyReferralLink = () => {
    const link = `${window.location.origin}/register?ref=${user?.referral_code}`;
    navigator.clipboard.writeText(link);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleWithdraw = async (e) => {
    e.preventDefault();
    // If user has selected a saved method, post just the id + amount.
    if (!useManualEntry && selectedMethodId) {
      setWithdrawing(true);
      try {
        const { data } = await api.post('/api/referrals/withdraw', {
          amount: parseFloat(parseFloat(withdrawAmount).toFixed(2)),
          payout_method_id: selectedMethodId,
        });
        const sched = data?.schedule;
        toast.success(sched
          ? `Withdrawal scheduled — payout on ${sched.next_payout}`
          : 'Withdrawal request submitted');
        setLastWithdrawalReceipt(data?.withdrawal || null);
        setWithdrawOpen(false);
        setWithdrawAmount('');
        fetchData();
      } catch (error) {
        console.error('Withdrawal failed:', error);
        toast.error(error.response?.data?.detail || 'Withdrawal failed');
      } finally {
        setWithdrawing(false);
      }
      return;
    }
    // Manual entry path (legacy fallback)
    // Method-specific validation
    if (payoutMethod === 'bank' && !bankName) {
      toast.warning('Please select a bank');
      return;
    }
    if (!bankAccountName || !bankAccountNumber) {
      toast.warning(payoutMethod === 'bank'
        ? 'Please fill in account name and account number'
        : 'Please fill in your name and mobile number');
      return;
    }
    setWithdrawing(true);
    try {
      const { data } = await api.post('/api/referrals/withdraw', {
        amount: parseFloat(parseFloat(withdrawAmount).toFixed(2)),
        method: payoutMethod,
        account_name: bankAccountName,
        account_number: bankAccountNumber,
        bank_name: payoutMethod === 'bank' ? bankName : null,
      });
      const sched = data?.schedule;
      toast.success(sched
        ? `Withdrawal scheduled — payout on ${sched.next_payout}`
        : 'Withdrawal request submitted');
      setLastWithdrawalReceipt(data?.withdrawal || null);
      setWithdrawOpen(false);
      setWithdrawAmount('');
      setBankName('');
      setBankAccountName('');
      setBankAccountNumber('');
      setBankSearch('');
      fetchData();
    } catch (error) {
      console.error('Withdrawal failed:', error);
      toast.error(error.response?.data?.detail || 'Withdrawal failed');
    } finally {
      setWithdrawing(false);
    }
  };

  const filteredBanks = banks.filter(b => b.name.toLowerCase().includes(bankSearch.toLowerCase()));

  const selectBank = (name) => {
    setBankName(name);
    setBankSearch(name);
    setShowBankDropdown(false);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-PH', {
      style: 'currency',
      currency: 'PHP',
      minimumFractionDigits: 0
    }).format(amount || 0);
  };

  if (loading) {
    return (
      <MemberSidebarLayout>
        <div className="min-h-screen flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-[#00E5FF]" />
        </div>
      </MemberSidebarLayout>
    );
  }

  return (
    <MemberSidebarLayout>
      <div className="p-4 sm:p-6 lg:p-8">
          <div className="mb-8">
            <h1 className="font-outfit text-2xl sm:text-3xl font-semibold text-white">Referral Program</h1>
            <p className="text-[#A0B4CC] mt-1">Invite friends and earn commission on their subscriptions</p>
          </div>

          <Card className="bg-gradient-to-r from-[#00E5FF]/10 to-[#007AFF]/10 border-[#00E5FF]/30 mb-8 overflow-hidden">
            <CardContent className="p-4 sm:p-6">
              <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4 md:gap-6">
                <div className="w-full md:flex-1 min-w-0">
                  <h3 className="font-outfit text-lg sm:text-xl font-semibold text-white mb-2 break-words">Share Your Referral Link</h3>
                  <p className="text-[#A0B4CC] text-xs sm:text-sm mb-4 break-words">
                    Earn commission on every subscription from your direct referrals
                  </p>
                  <div className="flex flex-wrap items-center gap-2 sm:gap-3">
                    <div className="px-3 sm:px-4 py-2 rounded-lg bg-[#000B18] border border-white/20 min-w-0">
                      <span className="font-mono text-sm sm:text-lg text-[#00E5FF] break-all">{user?.referral_code}</span>
                    </div>
                    <Button onClick={copyReferralLink} className="btn-primary" size="sm" data-testid="copy-link-btn">
                      {copied ? <CheckCircle className="w-4 h-4 mr-2" /> : <Copy className="w-4 h-4 mr-2" />}
                      {copied ? 'Copied!' : 'Copy Link'}
                    </Button>
                    <ReferralShareButton referralCode={user?.referral_code} userName={user?.name} />
                  </div>
                </div>
                <div className="text-left md:text-right w-full md:w-auto">
                  <p className="text-[#A0B4CC] text-xs sm:text-sm">Total Earnings</p>
                  <p className="font-outfit text-2xl sm:text-3xl font-bold text-[#00E676] break-words"><DualPrice php={stats?.total_earnings || 0} phpDecimals={2} usdClassName="text-sm text-[#A0B4CC] font-medium ml-1" /></p>
                </div>
              </div>
            </CardContent>
          </Card>

          <div className="grid grid-cols-2 gap-4 mb-8">
            <Card className="bg-[#041A3B] border-white/10">
              <CardContent className="p-4 text-center">
                <p className="text-3xl font-bold text-[#00E5FF]">{stats?.direct_count || stats?.total_referrals || 0}</p>
                <p className="text-[#A0B4CC] text-sm">Direct Referrals</p>
              </CardContent>
            </Card>
            <Card className="bg-[#041A3B] border-white/10">
              <CardContent className="p-4 text-center">
                <p className="text-3xl font-bold text-[#00E676]"><DualPrice php={stats?.total_earnings || 0} phpDecimals={2} usdClassName="text-xs text-[#A0B4CC] font-medium ml-1" /></p>
                <p className="text-[#A0B4CC] text-sm">Total Commissions</p>
              </CardContent>
            </Card>
          </div>

          <EarningStreakCard />

          <Card className="bg-[#041A3B] border-white/10 mb-8">
            <CardContent className="p-6">
              <div className="flex flex-col md:flex-row items-center justify-between gap-4">
                <div>
                  <h3 className="font-outfit text-lg font-semibold text-white">Available for Withdrawal</h3>
                  <p className="text-[#00E676] text-2xl font-bold"><DualPrice php={stats?.total_earnings || 0} phpDecimals={2} usdClassName="text-sm text-[#A0B4CC] font-medium ml-1" /></p>
                  <p className="text-[#5C7599] text-sm">Pending: <DualPrice php={stats?.pending_withdrawal || 0} phpDecimals={2} usdClassName="opacity-70" /></p>
                </div>
                <Dialog open={withdrawOpen} onOpenChange={setWithdrawOpen}>
                  <DialogTrigger asChild>
                    <Button className="btn-primary" disabled={(stats?.total_earnings || 0) < 100} data-testid="withdraw-btn">
                      <DollarSign className="w-4 h-4 mr-2" />
                      Withdraw
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="bg-[#041A3B] border-white/10 max-w-md">
                    <DialogHeader>
                      <DialogTitle className="text-white">Request Withdrawal</DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleWithdraw} className="space-y-4">
                      {/* Saved methods picker (when any) */}
                      {savedMethods.length > 0 && !useManualEntry && (
                        <div className="space-y-2">
                          <Label className="text-white">Send To</Label>
                          <div className="space-y-2" data-testid="saved-method-picker">
                            {savedMethods.map((m) => (
                              <label
                                key={m.id}
                                className={`flex items-center justify-between gap-3 p-3 rounded-md border cursor-pointer transition-colors ${
                                  selectedMethodId === m.id
                                    ? 'bg-[#00E5FF]/10 border-[#00E5FF]'
                                    : 'bg-[#000B18] border-white/20 hover:border-white/40'
                                }`}
                              >
                                <div className="flex items-center gap-2 min-w-0">
                                  <input
                                    type="radio"
                                    name="saved-method"
                                    value={m.id}
                                    checked={selectedMethodId === m.id}
                                    onChange={() => setSelectedMethodId(m.id)}
                                    className="accent-[#00E5FF]"
                                    data-testid={`saved-method-${m.id}`}
                                  />
                                  <div className="min-w-0">
                                    <p className="text-sm text-white truncate">{m.label || m.bank_name}</p>
                                    <p className="text-[#5C7599] text-xs truncate">{m.bank_name} · {m.account_number_masked || '••••'}</p>
                                  </div>
                                </div>
                                {m.is_default && (
                                  <span className="text-[10px] text-[#00E676]">Default</span>
                                )}
                              </label>
                            ))}
                          </div>
                          <button
                            type="button"
                            onClick={() => setUseManualEntry(true)}
                            className="text-xs text-[#00E5FF] hover:underline"
                            data-testid="switch-manual-entry"
                          >
                            + Use a different account (one-time)
                          </button>
                        </div>
                      )}

                      {savedMethods.length === 0 && (
                        <div className="rounded-md bg-[#FFEA00]/5 border border-[#FFEA00]/20 p-3 text-xs text-[#A0B4CC]">
                          💡 Tip: Add a payout method on your{' '}
                          <a href="/wallet" className="text-[#00E5FF] hover:underline">Wallet</a>{' '}
                          to skip filling out details every time and enable automated PayMongo payouts.
                        </div>
                      )}

                      {(useManualEntry || savedMethods.length === 0) && (
                        <>
                          {savedMethods.length > 0 && (
                            <button
                              type="button"
                              onClick={() => setUseManualEntry(false)}
                              className="text-xs text-[#00E5FF] hover:underline"
                              data-testid="switch-saved-methods"
                            >
                              ← Use a saved payout method
                            </button>
                          )}
                      {/* Method picker */}
                      <div className="space-y-2">
                        <Label className="text-white">Payout Method</Label>
                        <div className="grid grid-cols-3 gap-2">
                          {[
                            { v: 'gcash', label: 'GCash', emoji: '💙' },
                            { v: 'paymaya', label: 'PayMaya', emoji: '💚' },
                            { v: 'bank', label: 'Bank', emoji: '🏦' },
                          ].map((m) => (
                            <button
                              key={m.v}
                              type="button"
                              onClick={() => setPayoutMethod(m.v)}
                              className={`p-3 rounded-md border text-sm font-medium transition-colors ${
                                payoutMethod === m.v
                                  ? 'bg-[#00E5FF]/15 border-[#00E5FF] text-[#00E5FF]'
                                  : 'bg-[#000B18] border-white/20 text-[#A0B4CC] hover:border-white/40'
                              }`}
                              data-testid={`payout-method-${m.v}`}
                            >
                              <div className="text-lg leading-none mb-1">{m.emoji}</div>
                              {m.label}
                            </button>
                          ))}
                        </div>
                      </div>

                      {/* Bank Name with auto-suggest — only for bank method */}
                      {payoutMethod === 'bank' && (
                      <div className="space-y-2 relative">
                        <Label className="text-white">Bank Name</Label>
                        <Input
                          type="text"
                          value={bankSearch}
                          onChange={(e) => { setBankSearch(e.target.value); setShowBankDropdown(true); setBankName(''); }}
                          onFocus={() => setShowBankDropdown(true)}
                          placeholder="Type to search bank..."
                          required={payoutMethod === 'bank'}
                          className="bg-[#000B18] border-white/20 text-white"
                          data-testid="bank-name-input"
                        />
                        {showBankDropdown && bankSearch && filteredBanks.length > 0 && (
                          <div className="absolute z-50 w-full mt-1 max-h-48 overflow-y-auto rounded-md bg-[#000B18] border border-white/20 shadow-lg">
                            {filteredBanks.slice(0, 20).map(b => (
                              <button key={b.id} type="button" onClick={() => selectBank(b.name)}
                                className="w-full text-left px-3 py-2 text-sm text-white hover:bg-[#041A3B] transition-colors">
                                {b.name}
                              </button>
                            ))}
                          </div>
                        )}
                        {bankName && <p className="text-[#00E676] text-xs">Selected: {bankName}</p>}
                      </div>
                      )}

                      <div className="space-y-2">
                        <Label className="text-white">{payoutMethod === 'bank' ? 'Account Name' : 'Your Full Name'}</Label>
                        <Input
                          type="text"
                          value={bankAccountName}
                          onChange={(e) => setBankAccountName(e.target.value)}
                          placeholder={payoutMethod === 'bank' ? "Account holder's full name" : "Juan Dela Cruz"}
                          required
                          className="bg-[#000B18] border-white/20 text-white"
                          data-testid="bank-account-name-input"
                        />
                      </div>

                      <div className="space-y-2">
                        <Label className="text-white">{payoutMethod === 'bank' ? 'Account Number' : 'Mobile Number'}</Label>
                        <Input
                          type="text"
                          value={bankAccountNumber}
                          onChange={(e) => setBankAccountNumber(e.target.value)}
                          placeholder={payoutMethod === 'bank' ? 'Account number' : '+639XXXXXXXXX'}
                          required
                          className="bg-[#000B18] border-white/20 text-white"
                          data-testid="bank-account-number-input"
                        />
                      </div>
                        </>
                      )}

                      <div className="space-y-2">
                        <Label className="text-white">Amount (min. PHP 100)</Label>
                        <Input
                          type="number"
                          min="100"
                          step="0.01"
                          max={stats?.total_earnings}
                          value={withdrawAmount}
                          onChange={(e) => setWithdrawAmount(e.target.value)}
                          required
                          placeholder="0.00"
                          className="bg-[#000B18] border-white/20 text-white"
                          data-testid="withdraw-amount-input"
                        />
                      </div>

                      {payoutSchedule && (
                        <div className="rounded-md bg-[#00E5FF]/5 border border-[#00E5FF]/20 p-3 text-xs text-[#A0B4CC]">
                          📅 Next cut-off: <span className="text-white">{payoutSchedule.next_cutoff}</span> · Next payout: <span className="text-white">{payoutSchedule.next_payout}</span>
                          <div className="mt-1 text-[#5C7599]">Submitting before the cut-off gets you paid on the next payout date.</div>
                        </div>
                      )}

                      <Button
                        type="submit"
                        disabled={
                          withdrawing
                          || (!useManualEntry && savedMethods.length > 0 && !selectedMethodId)
                          || ((useManualEntry || savedMethods.length === 0) && payoutMethod === 'bank' && !bankName)
                        }
                        className="w-full btn-primary"
                        data-testid="submit-withdraw-btn"
                      >
                        {withdrawing ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Submit Request'}
                      </Button>
                    </form>
                  </DialogContent>
                </Dialog>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-[#041A3B] border-white/10 mb-8">
            <CardHeader>
              <CardTitle className="font-outfit text-white flex items-center gap-2">
                <Users className="w-5 h-5 text-[#00E5FF]" />
                Your Referral Network
                <span className="ml-2 text-xs font-normal text-[#5C7599]">
                  L1 · {tree.totals?.level_1 || 0} &nbsp;·&nbsp; L2 · {tree.totals?.level_2 || 0}
                </span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              {(!tree.level_1 || tree.level_1.length === 0) ? (
                <div className="py-12 text-center">
                  <Users className="w-12 h-12 text-[#5C7599] mx-auto mb-4" />
                  <p className="text-[#A0B4CC]">No referrals yet. Share your link to start earning!</p>
                </div>
              ) : (
                <div className="space-y-6" data-testid="referral-tree">
                  {/* ── Level 1 ── */}
                  <div>
                    <p className="text-[10px] uppercase tracking-widest text-[#00E5FF] mb-2">
                      Level 1 · Direct referrals
                    </p>
                    <div className="space-y-3">
                      {tree.level_1.map((ref, i) => {
                        const isAdminActivated = !!ref.admin_activated;
                        const l2ForRef = (tree.level_2 || []).filter((c) => c.via_id === ref.id);
                        return (
                          <div key={ref.id || ref.email || `ref-${i}`} data-testid={`referral-l1-row-${ref.id || i}`}>
                            <div
                              className={`p-4 rounded-lg bg-[#000B18] border-l-4 ${isAdminActivated ? 'border-[#5C7599] opacity-60' : 'border-[#00E5FF]'}`}
                              title={isAdminActivated ? 'Admin-activated account — no bonus earned' : undefined}
                              data-admin-activated={isAdminActivated ? 'true' : 'false'}
                            >
                              <div className="flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                  <div className={`w-10 h-10 rounded-full flex items-center justify-center ${isAdminActivated ? 'bg-[#0A1B2E]' : 'bg-[#041A3B]'}`}>
                                    <Users className={`w-5 h-5 ${isAdminActivated ? 'text-[#5C7599]' : 'text-[#00E5FF]'}`} />
                                  </div>
                                  <div>
                                    <p className={`font-medium ${isAdminActivated ? 'text-[#A0B4CC]' : 'text-white'}`}>{ref.name}</p>
                                    <p className="text-[#5C7599] text-xs">{ref.email}</p>
                                  </div>
                                </div>
                                <div className="flex flex-col items-end gap-1">
                                  <span className={`px-2 py-1 rounded text-xs ${
                                    ref.subscription_status === 'active' && !isAdminActivated
                                      ? 'bg-[#00E676]/20 text-[#00E676]'
                                      : 'bg-[#5C7599]/20 text-[#5C7599]'
                                  }`}>
                                    {ref.subscription_status || 'inactive'}
                                  </span>
                                  {isAdminActivated && (
                                    <span
                                      className="px-2 py-0.5 rounded-full text-[9px] font-semibold uppercase tracking-wide bg-amber-400/10 text-amber-400 border border-amber-400/30"
                                      data-testid={`referral-admin-activated-tag-${ref.id || i}`}
                                    >
                                      No bonus
                                    </span>
                                  )}
                                  {l2ForRef.length > 0 && (
                                    <span className="text-[9px] uppercase tracking-widest text-[#5C7599]">
                                      +{l2ForRef.length} L2
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>
                            {/* ── Level 2 nested under this L1 ── */}
                            {l2ForRef.length > 0 && (
                              <div className="mt-2 ml-6 pl-4 border-l border-dashed border-white/10 space-y-2">
                                {l2ForRef.map((c, ci) => {
                                  const cAdmin = !!c.admin_activated;
                                  return (
                                    <div
                                      key={c.id || `l2-${ci}`}
                                      className={`p-3 rounded-md bg-[#000B18]/60 border-l-2 ${cAdmin ? 'border-[#5C7599]/50 opacity-60' : 'border-[#00E5FF]/40'}`}
                                      data-testid={`referral-l2-row-${c.id || ci}`}
                                    >
                                      <div className="flex items-center justify-between">
                                        <div className="flex items-center gap-2 min-w-0">
                                          <Users className="w-3.5 h-3.5 text-[#5C7599] flex-shrink-0" />
                                          <div className="min-w-0">
                                            <p className={`text-sm truncate ${cAdmin ? 'text-[#A0B4CC]' : 'text-white'}`}>{c.name}</p>
                                            <p className="text-[#5C7599] text-[10px] truncate">{c.email}</p>
                                          </div>
                                        </div>
                                        <span className={`px-1.5 py-0.5 rounded text-[10px] ${
                                          c.subscription_status === 'active' && !cAdmin
                                            ? 'bg-[#00E676]/20 text-[#00E676]'
                                            : 'bg-[#5C7599]/20 text-[#5C7599]'
                                        }`}>
                                          {c.subscription_status || 'inactive'}
                                        </span>
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          <Card className="bg-[#041A3B] border-white/10">
            <CardHeader>
              <CardTitle className="font-outfit text-white">Commission History</CardTitle>
            </CardHeader>
            <CardContent>
              {commissions.length === 0 ? (
                <div className="py-8 text-center text-[#5C7599]">
                  No commissions yet
                </div>
              ) : (
                <div className="space-y-3">
                  {commissions.map((c, i) => (
                    <div key={c.id || `comm-${i}`} className="flex items-center justify-between p-3 rounded-lg bg-[#000B18]">
                      <div>
                        <p className="text-white">Direct Referral Commission</p>
                        <p className="text-[#5C7599] text-sm">{new Date(c.created_at).toLocaleDateString()}</p>
                      </div>
                      <span className="font-mono text-[#00E676]">+{formatCurrency(c.amount)}</span>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
      </div>
    </MemberSidebarLayout>
  );
}
